import type { ReactNode } from 'react'
import { faTrashCan } from '@fortawesome/pro-regular-svg-icons'
import { Badge, Text, VerticalThreeDotMenu } from '@/ui'
import { TextColors, TypographyStyles, mtc } from '@/styles'
import { formatTimeOrMonthDay } from '@/utils/dateHelpers'
import type {
  ConversationSummary,
  CustomerCaseEvent,
  CustomerCaseSummary,
} from '@/features/adviser-chat/lib/types'
import { OperatorAvatar } from './OperatorAvatar'

const DELETE_OPTION = {
  key: 'delete',
  label: 'Delete conversation',
  icon: faTrashCan,
}

interface ConversationHeaderProps {
  conversation: Omit<ConversationSummary, 'userId'> | undefined
  caseEvents?: CustomerCaseEvent[]
  caseHistoryEnabled?: boolean
  stacked?: boolean
  onDelete: (id: number) => void
}

const isOpenCase = (customerCase: CustomerCaseSummary): boolean =>
  !customerCase.returnedToHaven && customerCase.status !== 'done'

const isYourTurn = (customerCase: CustomerCaseSummary): boolean =>
  isOpenCase(customerCase) && customerCase.waitingOn === 'customer'

const statusLabel = (
  customerCase: CustomerCaseSummary,
  caseEvents: CustomerCaseEvent[]
): string => {
  if (customerCase.returnedToHaven) return 'With Haven'
  if (customerCase.status === 'done') {
    const resolution = [...caseEvents]
      .reverse()
      .find(
        (event) => event.kind === 'resolved' && event.caseId === customerCase.id
      )
    if (resolution?.kind === 'resolved' && resolution.resolver?.friendlyName) {
      return `Resolved by ${resolution.resolver.friendlyName}`
    }
    return 'Resolved'
  }
  if (customerCase.waitingOn === 'customer') return 'Your turn'
  return 'Waiting on Heard'
}

export const ConversationHeader = ({
  conversation,
  caseEvents = [],
  caseHistoryEnabled = false,
  stacked = false,
  onDelete,
}: ConversationHeaderProps) => {
  const customerCase = caseHistoryEnabled ? conversation?.case : null
  const activityAt =
    conversation?.lastActivity?.at ??
    conversation?.lastVisibleReplyAt ??
    conversation?.updatedAt
  const activityLabel =
    customerCase &&
    !conversation?.lastActivity &&
    !conversation?.lastVisibleReplyAt
      ? 'Updated'
      : 'Last reply'

  let statusChip: ReactNode = null
  if (customerCase) {
    const label = statusLabel(customerCase, caseEvents)
    if (isYourTurn(customerCase)) {
      statusChip = (
        <div className="flex shrink-0 items-center gap-2">
          <span className="size-2 rounded-full bg-orange-100" />
          <Text variant={TypographyStyles.LABEL_XS} color={TextColors.ORANGE}>
            {label}
          </Text>
        </div>
      )
    } else if (isOpenCase(customerCase)) {
      statusChip = (
        <div className="flex shrink-0 items-center gap-2">
          <span className="size-2 rounded-full bg-neutral-400" />
          <Text variant={TypographyStyles.LABEL_XS} color={TextColors.MUTED}>
            {label}
          </Text>
        </div>
      )
    } else {
      statusChip = (
        <Badge
          variant="secondary"
          size="sm"
          color="olive"
          className={stacked ? 'max-w-full' : 'max-w-full sm:max-w-[45%]'}
        >
          {label}
        </Badge>
      )
    }
  }

  const title = (
    <Text
      variant={TypographyStyles.LABEL_MD}
      color={TextColors.HEADER}
      className="truncate"
    >
      {conversation
        ? conversation.title || 'Untitled conversation'
        : 'New conversation'}
    </Text>
  )

  const activity = conversation && (
    <Text
      variant={TypographyStyles.BODY_XS}
      color={TextColors.MUTED}
      className="shrink-0 sm:shrink sm:truncate"
    >
      {activityLabel}{' '}
      {formatTimeOrMonthDay(activityAt || conversation.updatedAt)}
    </Text>
  )

  const ownerChip = customerCase?.status !== 'done' && customerCase?.owner && (
    <div
      className={mtc(
        'flex w-fit min-w-0 max-w-full items-center gap-2 rounded-full border border-neutral-200 py-0.5 pl-1 pr-2',
        !stacked && 'sm:max-w-[35%]'
      )}
    >
      <OperatorAvatar
        operator={customerCase.owner}
        className="size-5 text-[10px]"
      />
      <Text
        variant={TypographyStyles.LABEL_XS}
        color={TextColors.HEADER}
        className="truncate"
      >
        With {customerCase.owner.friendlyName}
      </Text>
    </div>
  )

  const menu = conversation && (
    <VerticalThreeDotMenu
      options={[DELETE_OPTION]}
      onSelect={() => onDelete(conversation.id)}
    />
  )

  if (stacked && customerCase) {
    return (
      <div
        data-testid="conversation-header-stacked"
        className="flex flex-col gap-1 shrink-0 px-6 py-3 border-b border-neutral-100"
      >
        <div className="flex items-center gap-4">
          <div className="flex flex-1 min-w-0">{title}</div>
          {menu}
        </div>
        <div className="flex h-7 items-center gap-x-3 overflow-hidden">
          {ownerChip}
          {statusChip}
          <span className="hidden sm:block ml-auto">{activity}</span>
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-wrap items-center gap-4 shrink-0 px-6 py-3 border-b border-neutral-100 sm:flex-nowrap">
      <div className="flex flex-wrap items-center flex-1 min-w-0 overflow-hidden gap-x-3 gap-y-1 sm:flex-nowrap">
        {title}
        {activity}
      </div>
      {statusChip}
      {ownerChip}
      {menu}
    </div>
  )
}
